import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/components/Icon';
import { useLocation } from 'react-router';

const MenuContext = createContext();

export const MenuProvider = ({ children }) => {
  const [menuState, setMenuState] = useState({
    isOpen: false,
    x: 0,
    y: 0,
    items: [],
    rightHanded: false
  });

  const openMenu = useCallback((x, y, items, rightHanded) => setMenuState({ isOpen: true, x, y, items, rightHanded }), []);
  const closeMenu = useCallback(() => setMenuState(prev => ({ ...prev, isOpen: false })), []);

  return (
    <MenuContext.Provider value={{ menuState, openMenu, closeMenu }}>
      {children}
      <Menu />
    </MenuContext.Provider>
  );
};

const Menu = () => {
  const { menuState, closeMenu } = useContext(MenuContext);
  const menuRef = useRef(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const [returnFocusToElement, setReturnFocusToElement] = useState(null);
  const location = useLocation();

  const buttonItems = menuState.items.filter(item => item !== "separator" && !item.heading);
  const enabledButtonItems = buttonItems.filter(item => !item.disabled);

  useEffect(() => {
    closeMenu();
  }, [location.key, closeMenu]);

  const handleKeyDown = useCallback((e) => {
    if (!menuState.isOpen) return;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeMenu();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (enabledButtonItems.length > 0) {
          setFocusIndex((prevIndex) => {
            let newIndex = (prevIndex + 1) % enabledButtonItems.length;
            return newIndex;
          });
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (enabledButtonItems.length > 0) {
          setFocusIndex((prevIndex) => 
            (prevIndex - 1 + enabledButtonItems.length) % enabledButtonItems.length
          );
        }
        break;
      case 'Tab':
        e.preventDefault(); // Prevent tabbing out of the menu
        break;
    }
  }, [menuState.isOpen, enabledButtonItems.length, closeMenu]);

  useEffect(() => {
    if (menuState.isOpen) {
      setReturnFocusToElement(document.activeElement);
      menuRef.current?.focus();
    } else {
      returnFocusToElement?.focus();
      setFocusIndex(0);
    }

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuState.isOpen, closeMenu]);

  useEffect(() => {
    if (menuState.isOpen && menuRef.current) {
      const menuItems = menuRef.current.querySelectorAll('[role="menuitem"]:not(.disabled)');
      menuItems[focusIndex]?.focus();
    }
  }, [focusIndex, menuState.isOpen]);

  if (!menuState.isOpen) return null;

  const menuPaddingHeight = 10 + 10; // padding top + bottom + margin
  let menuHeight = menuState.items.map(a => a == "separator" ? 11 : (a.heading ? 31 : 40)).reduce((a, b) => a + b, (5 + 5));
  let menuWidth = 200;

  // Calculate available space (fit within screen)
  const spaceBelow = window.innerHeight - menuState.y - 4;
  const spaceAbove = menuState.y;

  let openAbove = false;

  // If there's not enough space below, but there is enough space above, open above
  if ((menuHeight + 7) > spaceBelow && (menuHeight + 7) < spaceAbove) {
    openAbove = true;
  }

  // If there's not enough space below, and the space above is at least 1.5x the space below, open above
  if ((menuHeight + 7) > spaceBelow && (spaceBelow * 1.5) < spaceAbove) {
    openAbove = true;
  }

  let menuPosition = {
    left: menuState.x - 1,
  }

  if (openAbove) {
    menuPosition.top = Math.max(spaceAbove - menuHeight, 7) + "px";
    menuPosition.maxHeight = (spaceAbove - menuPaddingHeight - 7) + "px";
  } else {
    menuPosition.top = (menuState.y + 4) + "px";
    menuPosition.maxHeight = (spaceBelow - menuPaddingHeight - 7) + "px";
  }

  if (menuPosition.left + menuWidth > window.innerWidth || menuState.rightHanded) {
    menuPosition.right = window.innerWidth - menuPosition.left;
    delete menuPosition.left;
  }

  const openModals = document.querySelectorAll('dialog[open]');
  const isInsideModal = openModals.length > 0;
  const portalTarget = isInsideModal ? openModals[openModals.length - 1] : document.body;

  return createPortal(
    <ul
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className={"context_menu" + (isInsideModal ? " context_menu_in_modal" : "")}
      style={menuPosition}
    >
      {menuState.items.map((item, index) => {
        if (item == "separator") {
          return <li key={index} className="context_menu_separator"></li>;
        } else if (item.heading) {
          return <li key={index} className="context_menu_heading"><div>{item.heading}</div></li>;
        } else {
          return (
            <li
              key={index}
              role="menuitem"
              className={`context_menu_item ${item.disabled ? 'disabled' : ''}`}
              tabIndex={item.disabled ? undefined : -1}
              onClick={() => {
                if (item.disabled) return;
                item.onClick?.();
                closeMenu();
              }}
              aria-disabled={item.disabled}
            >
              {item.icon && <Icon name={item.icon} />}
              <div>{item.label}</div>
            </li>
          )
        }
      })}
    </ul>,
    portalTarget
  );
};

export const useMenu = ({ menuItems, rightHanded = true }) => {
  const menuContext = useContext(MenuContext);

  const handleContextMenu = (e) => {
    e.preventDefault();
    menuContext.openMenu(e.clientX, e.clientY, menuItems, false);
  };

  const handleOnClick = (e) => {
    let {x, y, height, width} = e.target.closest('button').getBoundingClientRect();
    e.preventDefault();
    e.stopPropagation();
    menuContext.openMenu(x+(rightHanded ? width : 0), y+height-2, menuItems, rightHanded);
  }

  return {
    handleContextMenu,
    handleOnClick
  }
};